// Main Dashboard Component

import { useEffect, useRef } from 'react';
import { useTradingStore } from '../lib/store';
import { StrategyCard } from './StrategyCard';
import { TradeLog } from './TradeLog';
import { EquityCurve } from './EquityCurve';
import { MarketScanner } from './MarketScanner';
import { Controls } from './Controls';

export function Dashboard() {
  const config = useTradingStore((state) => state.config);
  const strategyA = useTradingStore((state) => state.strategyA);
  const strategyB = useTradingStore((state) => state.strategyB);
  const qualifyingA = useTradingStore((state) => state.qualifyingMarketsA);
  const qualifyingB = useTradingStore((state) => state.qualifyingMarketsB);
  const isRunning = useTradingStore((state) => state.isRunning);
  const refreshMarkets = useTradingStore((state) => state.refreshMarkets);
  const executeTrade = useTradingStore((state) => state.executeTrade);
  const simulateResolution = useTradingStore((state) => state.simulateResolution);
  
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // Auto-refresh markets
  useEffect(() => {
    if (isRunning) {
      // Initial fetch
      refreshMarkets();
      
      // Set up interval
      intervalRef.current = setInterval(() => {
        refreshMarkets();
      }, config.refreshIntervalMs);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, config.refreshIntervalMs]);
  
  // Auto-trading logic
  useEffect(() => {
    if (!isRunning || !config.autoTrade) return;
    
    // Check if we're in scheduled hours
    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = now.toTimeString().slice(0, 5);
    
    const isScheduledDay = config.scheduledDays.includes(currentDay);
    const isScheduledTime = config.scheduledStart && config.scheduledEnd && 
      currentTime >= config.scheduledStart && currentTime <= config.scheduledEnd;
    
    if (!isScheduledDay || !isScheduledTime) return;
    
    // Execute trades for qualifying markets (in simulation mode)
    if (config.mode === 'SIMULATION') {
      // Strategy A
      if (config.strategyAEnabled && qualifyingA.length > 0) {
        const randomMarket = qualifyingA[Math.floor(Math.random() * qualifyingA.length)];
        executeTrade('A', randomMarket);
      }
      
      // Strategy B
      if (config.strategyBEnabled && qualifyingB.length > 0) {
        const randomMarket = qualifyingB[Math.floor(Math.random() * qualifyingB.length)];
        executeTrade('B', randomMarket);
      }
    }
  }, [isRunning, config.autoTrade, qualifyingA, qualifyingB]);
  
  // Auto-resolve pending trades (simulation only)
  useEffect(() => {
    if (!isRunning || config.mode !== 'SIMULATION') return;
    
    const pendingA = strategyA.trades.filter(t => t.result === 'pending');
    const pendingB = strategyB.trades.filter(t => t.result === 'pending');
    
    // Randomly resolve old pending trades (older than 10 seconds)
    const now = Date.now();
    [...pendingA, ...pendingB].forEach(trade => {
      const age = now - new Date(trade.timestamp).getTime();
      if (age > 10000 && Math.random() < 0.3) { // 30% chance to resolve each tick
        simulateResolution(trade.id);
      }
    });
  }, [isRunning, strategyA.trades, strategyB.trades]);

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>📊 Kalshi USL Trader</h1>
        <p className="subtitle">
          Universal Saturation Law + Anomaly Detection
        </p>
      </header>
      
      <Controls />
      
      <div className="dashboard-grid">
        <div className="strategies-section">
          <StrategyCard
            strategy="A"
            state={strategyA}
            enabled={config.strategyAEnabled}
            qualifyingCount={qualifyingA.length}
          />
          <StrategyCard
            strategy="B"
            state={strategyB}
            enabled={config.strategyBEnabled}
            qualifyingCount={qualifyingB.length}
          />
        </div>
        
        <div className="chart-section">
          <EquityCurve />
        </div>
        
        <div className="scanner-section">
          <MarketScanner />
        </div>
        
        <div className="log-section">
          <TradeLog maxTrades={30} />
        </div>
      </div>
      
      <footer className="dashboard-footer">
        <p>
          Based on <a href="https://github.com/your-repo/usl">Universal Saturation Law</a> research.
          Strategy A: +26% historical return | Strategy B: +14% historical return
        </p>
        <p className="disclaimer">
          ⚠️ This is not financial advice. Past performance does not guarantee future results.
        </p>
      </footer>
    </div>
  );
}
